import { FC } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import UtilityButtons from '../buttons/UtilityButtons.component';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import CustomTable from '../../../../tables/CustomTable';
import { mainColors } from '../../../../../styling/theme';
import KidsEditorGridItem from '../../kidsV2/layout/KidsEditorGridItem.component';
import { EditorTileProps } from '../../kids/editor/editorTiles/KidsEditorColumn.component';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';

const useStyles = makeStyles(() => ({
  textEditorBox: {
    marginTop: '1.25rem',
    marginBottom: '1.25rem',
    marginLeft: '0.62rem',
    fontSize: '0.94rem',
    boxSizing: 'border-box',
    padding: '0.62rem',
    borderRadius: '6px',
    width: '100%',
    minHeight: '6.25rem',
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginRight: '0.62rem',
  },
  formatContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: '0.62rem',
    gap: '1.25rem',
  },
}));

interface PerformanceScenariosTableData {
  scenario: string;
  description: string;
  [key: string]: string | number;
}

function buildPerformanceScenariosTableData(
  figures_data: any,
): PerformanceScenariosTableData[] {
  const tableData: PerformanceScenariosTableData[] = [];

  const usedScenarios = figures_data?.used_scenarios || [];
  const scenarioDict = figures_data?.scenario_dictionary || {};

  const scenarioTypes = [
    { key: 'stressed', label: 'Stress' },
    { key: 'unfavourable', label: 'Unfavorable' },
    { key: 'moderate', label: 'Moderate' },
    { key: 'favourable', label: 'Favorable' },
  ];

  scenarioTypes.forEach(({ key, label }) => {
    const scenarioData = scenarioDict[key];
    if (!scenarioData) {
      console.warn(`Missing scenario data for key: ${key}`);
      return;
    }

    const valueRow: PerformanceScenariosTableData = {
      scenario: label,
      description: 'What you might get back after costs',
    };

    const returnRow: PerformanceScenariosTableData = {
      scenario: '',
      description: 'Average Return Each Year',
    };

    usedScenarios.forEach((year: string) => {
      const values = scenarioData?.[year];
      const yearKey = `year${year}` as keyof PerformanceScenariosTableData;

      if (!values || values.length < 2) {
        console.warn(`Missing data for scenario '${key}', year '${year}'`);
        valueRow[yearKey] = 'Error';
        returnRow[yearKey] = 'Error';
      } else {
        valueRow[yearKey] = `${addCommasToNumbersAndRound(values[1])}`;
        returnRow[yearKey] = `${percentageToTwoDecimalPlaces(values[0])}`;
      }
    });

    tableData.push(valueRow, returnRow);
  });

  return tableData;
}

function buildTableColumns(
  tableData: PerformanceScenariosTableData[],
  figures_data: any,
): CustomColumn<PerformanceScenariosTableData>[] {
  const columns: CustomColumn<PerformanceScenariosTableData>[] = [
    {
      title: 'Scenarios',
      field: 'scenario',
      cellStyle: {
        width: '20%',
        border: '1px solid #1875d2',
        padding: '0.31rem',
        textAlign: 'center',
        backgroundColor: 'white',
      },
      headerStyle: {
        width: '20%',
        padding: '0.31rem',
        textAlign: 'center',
        color: 'white',
        backgroundColor: mainColors.controlButtonBlue,
      },
    },
    {
      title: '',
      field: 'description',
      cellStyle: {
        width: '20%',
        border: '1px solid #1875d2',
        padding: '0.31rem',
        textAlign: 'center',
        backgroundColor: 'white',
      },
      headerStyle: {
        width: '20%',
        padding: '0.31rem',
        textAlign: 'center',
        color: 'white',
        backgroundColor: mainColors.controlButtonBlue,
      },
    },
  ];

  // Get the years from the first row of table data
  if (figures_data.used_scenarios.length > 0) {
    // Add a column for each year
    figures_data.used_scenarios.forEach((year: string) => {
      const isLastYear = year === String(figures_data.holding_period);
      columns.push({
        title: isLastYear
          ? `${year} Years (Recommended Holding Period)`
          : `${year} Year${year !== '1' ? 's' : ''}`,
        field: `year${year}`,
        cellStyle: {
          border: '1px solid #1875d2',
          padding: '0.31rem',
          textAlign: 'center',
          backgroundColor: 'white',
        },
        headerStyle: {
          padding: '0.31rem',
          textAlign: 'center',
          color: 'white',
          backgroundColor: mainColors.controlButtonBlue,
        },
      });
    });
  }

  return columns;
}

const PerformanceScenariosEditorTile: FC<EditorTileProps> = ({
  priipsKidsElement,
  moveField,
  deleteField,
}) => {
  // Store the index of the current element in the overall data
  const index = priipsKidsElement.kiidIndex;
  // Store the field ID of the element
  const fieldId = priipsKidsElement.fieldId;
  // Convert the content to a javascript object
  const content = JSON.parse(priipsKidsElement.content);
  // Build the table data
  const tableData = buildPerformanceScenariosTableData(content);
  // Build the table columns
  const columns = buildTableColumns(tableData, content);

  const classes = useStyles();

  return (
    <KidsEditorGridItem xs={12}>
      <div className={classes.editorContainer}>
        <div className={classes.textEditorBox}>
          <CustomTable<PerformanceScenariosTableData>
            columns={columns}
            id={'performance_scenarios_table'}
            showToolbar={false}
            data={tableData}
            options={{
              paging: false,
              search: false,
              exportButton: false,
              sorting: false,
              draggable: false,
              toolbar: false,
            }}
          />
        </div>
        <UtilityButtons
          moveField={moveField}
          index={index}
          deleteField={deleteField}
          fieldId={fieldId}
          disableDelete
          disableDeleteMessage="You cannot delete the performance scenarios table."
        />
      </div>
    </KidsEditorGridItem>
  );
};

export default PerformanceScenariosEditorTile;
